import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { MoreVert, Edit, Delete, PlayArrow, Pause } from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { CouponTemplateDto, CouponTemplateStatus } from "../../store/models/coupon/CouponManagementDto";
import { BooleanThunk } from "../../store/IStore";
import { Api } from "../../api/Api";
import KnistrConfirmDialog from "../atoms/KnistrConfirmDialog";
import { thunkActivateCouponTemplate, thunkDeactivateCouponTemplate } from "../../store/actions/CouponActions";
import { thunkCreateSuccessNotification, thunkCreateErrorNotification } from "../../store/actions/NotificationActions";

interface CouponTableMenuProps {
  coupon: CouponTemplateDto;
  onCouponUpdated?: () => void; // Changed to refresh callback
  onCouponDeleted?: (couponId: string) => void;
  thunkActivateCouponTemplate: (couponNumber: string) => BooleanThunk;
  thunkDeactivateCouponTemplate: (couponNumber: string) => BooleanThunk;
  thunkCreateSuccessNotification: (message: string) => void;
  thunkCreateErrorNotification: (message: string, error: any) => void;
}

const CouponTableMenu = ({ 
  coupon, 
  onCouponUpdated, 
  onCouponDeleted,
  thunkActivateCouponTemplate,
  thunkDeactivateCouponTemplate,
  thunkCreateSuccessNotification,
  thunkCreateErrorNotification
}: CouponTableMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (!coupon.couponTemplateNumber) {
      thunkCreateErrorNotification('Cannot edit coupon: Invalid coupon ID', null);
      handleClose();
      return;
    }
    navigate(`/loyalty/coupon/${coupon.couponTemplateNumber}/edit`, {
      state: { coupon: coupon }
    });
    handleClose();
  };


  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    if (!coupon.couponTemplateNumber) {
      thunkCreateErrorNotification('Cannot delete coupon: Invalid coupon ID', null);
      return;
    }
    try {
      setIsLoading(true);
      await Api.deleteCouponTemplate(coupon.couponTemplateNumber);
      onCouponDeleted?.(coupon.couponTemplateNumber);
      thunkCreateSuccessNotification('Coupon deleted successfully');
      // Refresh the coupon list
      onCouponUpdated?.();
    } catch (error) {
      console.error('Failed to delete coupon:', error);
      thunkCreateErrorNotification('Failed to delete coupon', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!coupon.couponTemplateNumber) {
      thunkCreateErrorNotification('Cannot activate coupon: Invalid coupon ID', null);
      handleClose();
      return;
    }
    try {
      setIsLoading(true);
      const success = await thunkActivateCouponTemplate(coupon.couponTemplateNumber);
      if (success) {
        // Refresh the coupon list
        onCouponUpdated?.();
      }
    } catch (error) {
      console.error('Failed to activate coupon:', error);
    } finally {
      setIsLoading(false);
    }
    handleClose();
  };

  const handleDeactivate = async () => {
    if (!coupon.couponTemplateNumber) {
      thunkCreateErrorNotification('Cannot deactivate coupon: Invalid coupon ID', null);
      handleClose();
      return;
    }
    try {
      setIsLoading(true);
      const success = await thunkDeactivateCouponTemplate(coupon.couponTemplateNumber);
      if (success) {
        // Refresh the coupon list
        onCouponUpdated?.();
      }
    } catch (error) {
      console.error('Failed to deactivate coupon:', error);
    } finally {
      setIsLoading(false);
    }
    handleClose();
  };

  return (
    <>
      {confirmDeleteOpen && (
        <KnistrConfirmDialog
          title={t("coupon.actions.deleteTitle", "Delete Coupon")}
          description={t("coupon.actions.deleteDescription", `Are you sure you want to delete "{{name}}"? This action cannot be undone.`, { name: coupon.name })}
          cancelButton={t("common.cancel")}
          confirmButton={t("common.delete")}
          onConfirm={handleDeleteConfirm}
          setConfirmVisible={setConfirmDeleteOpen}
        />
      )}
      <IconButton
        aria-label="more"
        id={`menu-button-${coupon.couponTemplateNumber}`}
        aria-controls={open ? `menu-${coupon.couponTemplateNumber}` : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id={`menu-${coupon.couponTemplateNumber}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': `menu-button-${coupon.couponTemplateNumber}`,
        }}
      >
        <MenuItem onClick={handleEdit} disabled={!coupon.couponTemplateNumber}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("coupon.actions.edit")}</ListItemText>
        </MenuItem>
        
        {/* Show activate option for inactive and draft coupons */}
        {(coupon.status === CouponTemplateStatus.DRAFT || coupon.status === CouponTemplateStatus.INACTIVE) && (
          <MenuItem onClick={handleActivate} disabled={isLoading || !coupon.couponTemplateNumber}>
            <ListItemIcon>
              <PlayArrow fontSize="small" />
            </ListItemIcon>
            <ListItemText>{isLoading ? 'Activating...' : t("coupon.actions.activate")}</ListItemText>
          </MenuItem>
        )}
        
        {/* Show deactivate option for active coupons */}
        {coupon.status === CouponTemplateStatus.ACTIVE && (
          <MenuItem onClick={handleDeactivate} disabled={isLoading || !coupon.couponTemplateNumber}>
            <ListItemIcon>
              <Pause fontSize="small" />
            </ListItemIcon>
            <ListItemText>{isLoading ? 'Deactivating...' : t("coupon.actions.deactivate")}</ListItemText>
          </MenuItem>
        )}
        
        
        {/* Show delete option only for draft coupons */}
        {coupon.status === CouponTemplateStatus.DRAFT && (
          <MenuItem onClick={handleDeleteClick} disabled={isLoading || !coupon.couponTemplateNumber}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>{isLoading ? 'Deleting...' : t("coupon.actions.delete")}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      thunkActivateCouponTemplate,
      thunkDeactivateCouponTemplate,
      thunkCreateSuccessNotification,
      thunkCreateErrorNotification,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(CouponTableMenu);